import { useEffect, useRef } from "react"
import { getConsumer } from "@/lib/cable"
import { camelizeKeys } from "@/lib/utils"

interface UseChannelOptions {
  channel: string
  params?: Record<string, unknown>
  onReceived: (data: Record<string, unknown>) => void
  // Fired when the subscription (re)connects — i.e. ActionCable's
  // `connected()` lifecycle callback. Use it to reconcile state
  // that may have drifted while the WebSocket was down (the
  // server is the source of truth). Fires on the initial connect
  // and on every automatic reconnect. (CON-151)
  onConnected?: () => void
}

// Opens an ActionCable subscription bound to the component
// lifecycle. Incoming payload keys are camelized so the
// React side reads `contactId` while the Rails side
// publishes `contact_id`. String values are passed through
// untouched — match snake_case for `type` discriminators.
export function useChannel({
  channel,
  params,
  onReceived,
  onConnected,
}: UseChannelOptions): void {
  const onReceivedRef = useRef(onReceived)
  onReceivedRef.current = onReceived

  const onConnectedRef = useRef(onConnected)
  onConnectedRef.current = onConnected

  const paramsKey = params ? JSON.stringify(params) : ""

  useEffect(() => {
    const subscription = getConsumer().subscriptions.create(
      { channel, ...(params ?? {}) },
      {
        connected() {
          onConnectedRef.current?.()
        },
        received(data: unknown) {
          if (data === null || typeof data !== "object") return
          const camelized = camelizeKeys(data) as Record<string, unknown>
          onReceivedRef.current(camelized)
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [channel, paramsKey])
}
